import { post, put } from "../../../../common/api/apiShared.ts";
import { FrameworkTemplateControlSaveModel } from "./FrameworkTemplateControlDialog.types.ts";
import { useState } from "react";
import { FrameworkTemplateControlDialogProps } from "./FrameworkTemplateControlDialog.tsx";
import { SelectableItem } from "../../../../processingActivity/ProcessingActivity.types.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { SelectableAnnualWheelActivityTableRow } from "../../../../common/components/selectableAnnualWheelActivityTable/SelectableAnnualWheelActivityTable.types.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useFrameworkTemplateControlDialog(props: FrameworkTemplateControlDialogProps) {
    const isEdit = props.control !== undefined && !!props.controlId;
    const snackbar = useDotLegalSnackbar();

    const url = `/frameworks/templates/${props.frameworkTemplateId}/categories/selectable`;
    const { isLoading: categoriesIsLoading, data: selectableCategories } = useDotLegalQuery<Array<SelectableItem>>(url);

    const annualWheelActivityTemplatesUrl = `/frameworks/templates/selectable-annual-wheel-activity-templates`;
    const { isLoading: annualWheelActivitiesIsLoading, data: selectableAnnualWheelActivities } =
        useDotLegalQuery<Array<SelectableAnnualWheelActivityTableRow>>(annualWheelActivityTemplatesUrl);

    const [saveModel, setSaveModel] = useState(createSaveModel(props.control));
    const [showError, setShowError] = useState(false);

    const modelUpdater = {
        onNameChange: (lang: string, name: string | undefined) => {
            let newModel = saveModel.withNewName(lang, name);
            setSaveModel(newModel);
        },
        onDescriptionChange: (lang: string, description: string | undefined) => {
            let newModel = saveModel.withNewDescription(lang, description);
            setSaveModel(newModel);
        },
        onCategoryChange: (categoryId: string | null) => {
            let newModel = saveModel.withUpdates({ categoryId: categoryId ?? "" });
            setSaveModel(newModel);
        },
        onImplementationGroupChange: (implementationGroup: string) => {
            let newModel = saveModel.withUpdates({ implementationGroup });
            setSaveModel(newModel);
        },
        onCustomIdChange: (customId: string | undefined) => {
            let newModel = saveModel.withUpdates({ customId });
            setSaveModel(newModel);
        },
        onAnnualWheelActivityChange: (annualWheelActivityId: string) => {
            if (saveModel.annualWheelActivities.includes(annualWheelActivityId)) {
                let newModel = saveModel.withUpdates({ annualWheelActivities: saveModel.annualWheelActivities.filter((x) => x !== annualWheelActivityId) });
                setSaveModel(newModel);
            } else {
                let newModel = saveModel.withUpdates({ annualWheelActivities: [...saveModel.annualWheelActivities, annualWheelActivityId] });
                setSaveModel(newModel);
            }
        },
    };

    const onSaveClick = async () => {
        if (saveModel.isInvalid()) {
            setShowError(true);
            return;
        }

        if (isEdit) {
            await editControl();
        } else {
            await createControl();
        }
    };

    const createControlMutation = useDotLegalMutation(createFrameworkTemplateControlApi);
    const createControl = async () => {
        await createControlMutation.mutateAsync(saveModel, {
            onSuccess: async (data: any) => {
                snackbar.show("Control created", "success");
                props.onControlSaved();
            },
        });
    };

    const editControlMutation = useDotLegalMutation(editFrameworkTemplateControlApi);
    const editControl = async () => {
        await editControlMutation.mutateAsync(saveModel, {
            onSuccess: async (data: any) => {
                snackbar.show("Control updated", "success");
                props.onControlSaved();
            },
        });
    };

    return {
        isEdit,
        saveModel,
        modelUpdater,
        showError,
        isSaving: createControlMutation.isLoading,
        categoriesIsLoading,
        selectableCategories,
        annualWheelActivitiesIsLoading,
        selectableAnnualWheelActivities,
        onSaveClick,
    };

    function createFrameworkTemplateControlApi(saveModel: FrameworkTemplateControlSaveModel) {
        return post(`/frameworks/templates/${props.frameworkTemplateId}/control`, saveModel);
    }

    function editFrameworkTemplateControlApi(saveModel: FrameworkTemplateControlSaveModel) {
        return put(`/frameworks/templates/${props.frameworkTemplateId}/control/${props.controlId}`, saveModel);
    }

    function createSaveModel(control?: FrameworkTemplateControlSaveModel) {
        return control ? control : FrameworkTemplateControlSaveModel.createDefault();
    }
}
